import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface UnsubscribeResponse {
  success: boolean;
  isTest?: boolean;
  alreadyUnsubscribed?: boolean;
  message: string;
  error?: string;
  website_url?: string;
}

export function Unsubscribe() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'test' | 'already'>('loading');
  const [message, setMessage] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('#');

  useEffect(() => {
    document.title = 'Uitschrijven';
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Ongeldige uitschrijflink');
      return;
    }

    const unsubscribe = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          },
          body: JSON.stringify({ token }),
        });

        const data: UnsubscribeResponse = await response.json();

        if (data.success) {
          const rawUrl = data.website_url || '#';
          setWebsiteUrl(rawUrl !== '#' && !/^https?:\/\//i.test(rawUrl) ? `https://${rawUrl}` : rawUrl);
          if (data.isTest) {
            setStatus('test');
            setMessage(data.message);
          } else if (data.alreadyUnsubscribed) {
            setStatus('already');
            setMessage(data.message);
          } else {
            setStatus('success');
            setMessage(data.message);
          }
        } else {
          setStatus('error');
          setMessage(data.error || 'Er is iets misgegaan bij het uitschrijven.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Er is een fout opgetreden. Probeer het later opnieuw.');
        console.error('Unsubscribe error:', error);
      }
    };

    unsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6">
              <Loader2 className="w-12 h-12 text-slate-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">
              Uitschrijven...
            </h1>
            <p className="text-slate-600">
              Een moment geduld
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-emerald-50 rounded-full">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-3">
              Uitgeschreven
            </h1>
            <p className="text-slate-600 mb-8">
              {message}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Je ontvangt geen emails meer van ons. We vinden het jammer om je te zien gaan.
            </p>
            <a
              href={websiteUrl}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Naar onze website
            </a>
          </div>
        )}

        {status === 'already' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-slate-50 rounded-full">
              <CheckCircle className="w-10 h-10 text-slate-500" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-3">
              Al uitgeschreven
            </h1>
            <p className="text-slate-600 mb-8">
              {message}
            </p>
            <a
              href={websiteUrl}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Naar onze website
            </a>
          </div>
        )}

        {status === 'test' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-blue-50 rounded-full">
              <AlertCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-3">
              Test Email
            </h1>
            <p className="text-slate-600 mb-4">
              {message}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
              <p className="text-sm text-blue-900">
                Deze link werd gebruikt in een test email. In echte emails krijgt elke ontvanger een unieke uitschrijflink.
              </p>
            </div>
            <a
              href={websiteUrl}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Naar onze website
            </a>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-red-50 rounded-full">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-3">
              Er is iets misgegaan
            </h1>
            <p className="text-slate-600 mb-8">
              {message}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              De uitschrijflink is mogelijk verlopen of ongeldig. Neem contact op als je hulp nodig hebt.
            </p>
            <a
              href={websiteUrl}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Naar onze website
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
